import { ColDef } from 'ag-grid-community';

import { DEFAULT_PLACEHOLDER_COLOR, SCHEDULE_COLUMN_WIDTH } from 'app/global/variables';

import { LookupTypeEnum } from 'app/graphql/generated/graphqlApolloTypes';

import { formatMessage } from 'utils/messages/utils';

import EditRowCellRenderer from './EditRowCellRenderer/EditRowCellRenderer';
import {
  onSuccess,
  syncValidator,
  syncValueSetter,
  valueValidator,
  percentageValueFormatter,
  scheduleNameValidator,
  ScheduleData
} from './LookupTableDetailUtil';

interface BuildLookupTableColumnDefProps {
  tableType: string;
  data: ScheduleData[];
  onDuplicateNameFound: (params) => () => void;
  handleAddNewRow: (params, tableType: string, data) => void;
}

export const buildLookupTableColumnDef = ({
  tableType,
  data,
  onDuplicateNameFound,
  handleAddNewRow
}: BuildLookupTableColumnDefProps): ColDef[] => {
  const monthColumnDefs: ColDef[] = [];
  const MIN_COLUMN_WIDTH = 81; // minimum width to avoid cutting off text
  Object.keys(data[0]).forEach((key) => {
    if (key.includes('month')) {
      monthColumnDefs.push({
        headerName: formatMessage('MONTH', { value: key.split('_')[1] }),
        field: key,
        flex: 1,
        minWidth: MIN_COLUMN_WIDTH,
        cellStyle: (params) => {
          if (params.node.rowPinned && isNaN(params.value)) {
            return { color: DEFAULT_PLACEHOLDER_COLOR };
          }
          return { color: 'rgb(var(--color-gray-2))' };
        },
        valueFormatter: (params) => {
          const defaultValue = tableType === LookupTypeEnum.Ramp ? 100 : 0;
          return `${(params.value >= 0 ? percentageValueFormatter(params.value) : defaultValue).toString()}%`;
        },
        valueSetter: (params) => syncValueSetter(params, valueValidator, tableType)
      });
    }
  });

  return [
    {
      headerName: formatMessage('SCHEDULE_NAME'),
      field: 'scheduleName',
      flex: 2,
      minWidth: SCHEDULE_COLUMN_WIDTH,
      valueFormatter: (params) => {
        return params.value ?? formatMessage('ENTER_NAME');
      },
      cellStyle: (params) => {
        if (params.node.rowPinned && !params.value) {
          return { color: DEFAULT_PLACEHOLDER_COLOR };
        }
        return { color: 'rgb(var(--color-gray-2))' };
      },
      valueGetter: (params) => {
        return params.data?.scheduleName;
      },
      valueSetter: (params) =>
        syncValidator(params, scheduleNameValidator, onSuccess(params), onDuplicateNameFound(params))
    },
    ...monthColumnDefs,
    {
      headerName: '',
      field: '',
      flex: 1,
      minWidth: MIN_COLUMN_WIDTH,
      editable: false,
      cellStyle: { border: 'none' },
      cellRendererSelector: (params) => {
        if (params?.node?.rowPinned) {
          return {
            component: EditRowCellRenderer,
            params: {
              onSaveButtonClicked: () => handleAddNewRow(params, tableType, data[0]),
              onCancelButtonClicked: () => {
                params.node.setData({});
              }
            }
          };
        }
        return null;
      }
    }
  ];
};
